import { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Typewriter } from 'react-simple-typewriter';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import profilePhoto from '../profilephoto.jpg';
import {
    personalInfo,
    heroRoles,
    heroLead,
    stats,
    aboutDescription,
    awards,
    experienceItems,
    skillGroups,
} from '../utils/constants';

const HeroScene = lazy(() => import('./FxHeroScene'));

export function SectionHeading({ id, index, kicker, title, children }) {
    return (
        <header className="fx-section__head" data-reveal>
            <p className="fx-kicker">
                <span className="fx-kicker__index">{index}</span>
                {kicker}
            </p>
            <h2 id={id} className="fx-section__title">{title}</h2>
            {children && <p className="fx-section__lead">{children}</p>}
        </header>
    );
}

SectionHeading.propTypes = {
    id: PropTypes.string.isRequired,
    index: PropTypes.string.isRequired,
    kicker: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    children: PropTypes.node,
};

export function SocialLinks({ className = '' }) {
    const links = [
        { href: personalInfo.github, label: 'GitHub', Icon: GitHubIcon },
        { href: personalInfo.linkedin, label: 'LinkedIn', Icon: LinkedInIcon },
        { href: `mailto:${personalInfo.email}`, label: 'Email', Icon: EmailOutlinedIcon },
    ];
    return (
        <ul className={`fx-social ${className}`.trim()}>
            {links.map(({ href, label, Icon }) => (
                <li key={label}>
                    <a
                        className="fx-icon-btn"
                        href={href}
                        aria-label={label}
                        target={href.startsWith('http') ? '_blank' : undefined}
                        rel="noopener noreferrer"
                    >
                        <Icon fontSize="small" />
                    </a>
                </li>
            ))}
        </ul>
    );
}

SocialLinks.propTypes = {
    className: PropTypes.string,
};

export function Hero({ reducedMotion = false }) {
    const [firstName, ...rest] = personalInfo.name.split(' ');
    return (
        <section id="home" className="fx-hero" aria-labelledby="hero-title">
            <Suspense fallback={null}>
                <HeroScene reducedMotion={reducedMotion} />
            </Suspense>
            <div className="fx-hero__glow" aria-hidden="true" />
            <div className="fx-container fx-hero__content">
                <p className="fx-eyebrow fx-enter" style={{ '--d': '0ms' }}>
                    <span className="fx-pulse" aria-hidden="true" />
                    <LocationOnOutlinedIcon fontSize="inherit" aria-hidden="true" />
                    {personalInfo.location}
                </p>
                <h1 id="hero-title" className="fx-hero__title fx-enter" style={{ '--d': '120ms' }}>
                    <span className="fx-hero__hi">Hi, I&apos;m</span>
                    <span className="fx-gradient-text fx-hero__name">
                        {firstName} <span className="fx-hero__surname">{rest.join(' ')}</span>
                    </span>
                </h1>
                <p className="fx-hero__role fx-enter" style={{ '--d': '240ms' }}>
                    <span className="fx-mono" aria-hidden="true">&gt;_ </span>
                    <span className="fx-sr-only">{personalInfo.title}</span>
                    <span aria-hidden="true">
                        {reducedMotion ? (
                            heroRoles[0]
                        ) : (
                            <Typewriter
                                words={heroRoles}
                                loop={0}
                                cursor
                                cursorStyle="▍"
                                typeSpeed={60}
                                deleteSpeed={35}
                                delaySpeed={1800}
                            />
                        )}
                    </span>
                </p>
                <p className="fx-hero__lead fx-enter" style={{ '--d': '360ms' }}>
                    {heroLead}
                </p>
                <div className="fx-hero__actions fx-enter" style={{ '--d': '480ms' }}>
                    <a className="fx-btn fx-btn--primary" href="#projects">
                        Explore my work
                        <ArrowOutwardIcon fontSize="small" aria-hidden="true" />
                    </a>
                    <a
                        className="fx-btn fx-btn--ghost"
                        href={personalInfo.resumePath}
                        download="Veeramalli_Sathwik_Resume.pdf"
                    >
                        <FileDownloadOutlinedIcon fontSize="small" aria-hidden="true" />
                        Download résumé
                    </a>
                    <SocialLinks />
                </div>
                <dl className="fx-stats fx-enter" style={{ '--d': '600ms' }}>
                    {stats.map((stat) => (
                        <div className="fx-stats__item" key={stat.label}>
                            <dt>{stat.label}</dt>
                            <dd className="fx-gradient-text">{stat.value}</dd>
                        </div>
                    ))}
                </dl>
            </div>
            <a className="fx-scroll-cue" href="#about" aria-label="Scroll to About section">
                <KeyboardArrowDownIcon aria-hidden="true" />
            </a>
        </section>
    );
}

Hero.propTypes = {
    reducedMotion: PropTypes.bool,
};

export function About() {
    const facts = [
        { label: 'Based in', value: personalInfo.location },
        { label: 'Building since', value: personalInfo.experienceSince },
        { label: 'Focus', value: 'React · .NET · Azure' },
    ];
    return (
        <section id="about" className="fx-section" aria-labelledby="about-title">
            <div className="fx-container">
                <SectionHeading id="about-title" index="01" kicker="About me" title={<>Engineering interfaces that <span className="fx-gradient-text">scale</span></>} />
                <div className="fx-about">
                    <figure className="fx-portrait" data-reveal>
                        <div className="fx-portrait__frame" aria-hidden="true">
                            <div className="fx-portrait__ring" />
                        </div>
                        <img
                            src={profilePhoto}
                            alt={`Portrait of ${personalInfo.name}`}
                            width="360"
                            height="440"
                            loading="lazy"
                            decoding="async"
                        />
                        <figcaption className="fx-portrait__badge">
                            <span className="fx-pulse" aria-hidden="true" />
                            {' '}{personalInfo.role} · {personalInfo.company}
                        </figcaption>
                    </figure>
                    <div className="fx-about__body">
                        <p className="fx-about__text" data-reveal>{aboutDescription.replaceAll(/\s+/g, ' ')}</p>
                        <dl className="fx-facts" data-reveal>
                            {facts.map((fact) => (
                                <div key={fact.label} className="fx-glass fx-facts__item">
                                    <dt>{fact.label}</dt>
                                    <dd>{fact.value}</dd>
                                </div>
                            ))}
                        </dl>
                        <article className="fx-glass fx-edu" data-reveal>
                            <span className="fx-edu__icon" aria-hidden="true">
                                <SchoolOutlinedIcon />
                            </span>
                            <div>
                                <h3 className="fx-edu__school">{personalInfo.college}</h3>
                                <p className="fx-edu__degree">{personalInfo.degree}</p>
                                <p className="fx-edu__meta">
                                    <span>{personalInfo.educationPeriod}</span>
                                    <span>CGPA {personalInfo.cgpa}</span>
                                </p>
                            </div>
                        </article>
                        <ul className="fx-awards" data-reveal>
                            {awards.map((award) => (
                                <li key={award.title} className="fx-glass fx-edu">
                                    <span className="fx-edu__icon" aria-hidden="true">
                                        <EmojiEventsOutlinedIcon />
                                    </span>
                                    <div>
                                        <h3 className="fx-edu__school">{award.title}</h3>
                                        <p className="fx-edu__degree">{award.detail}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function Experience() {
    return (
        <section id="experience" className="fx-section" aria-labelledby="experience-title">
            <div className="fx-container">
                <SectionHeading id="experience-title" index="02" kicker="Experience" title={<>Where I&apos;ve been <span className="fx-gradient-text">shipping</span></>} />
                <ol className="fx-timeline">
                    {experienceItems.map((job) => (
                        <li key={`${job.company}-${job.period}`} className="fx-timeline__item" data-reveal>
                            <span className="fx-timeline__dot" aria-hidden="true">
                                <WorkOutlineIcon fontSize="small" />
                            </span>
                            <article className="fx-glass fx-job">
                                <header className="fx-job__head">
                                    <div>
                                        <h3 className="fx-job__title">{job.title}</h3>
                                        <p className="fx-job__company">
                                            {job.company} · {job.location}
                                        </p>
                                    </div>
                                    <span className="fx-chip fx-chip--glow">{job.period}</span>
                                </header>
                                <ul className="fx-job__list">
                                    {job.achievements.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </article>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

export function Skills() {
    const allSkills = skillGroups.flatMap((group) => group.items);
    const marqueeItems = ['a', 'b'].flatMap((lap) =>
        allSkills.map((skill) => ({ id: `${lap}-${skill}`, skill }))
    );
    return (
        <section id="skills" className="fx-section" aria-labelledby="skills-title">
            <div className="fx-container">
                <SectionHeading id="skills-title" index="03" kicker="Toolkit" title={<>Skills &amp; <span className="fx-gradient-text">superpowers</span></>}>
                    The full-stack toolkit I use to design, build, test, secure
                    and ship production software.
                </SectionHeading>
                <div className="fx-skills">
                    {skillGroups.map((group) => (
                        <article
                            key={group.title}
                            className={`fx-glass fx-skill-card fx-accent--${group.accent}`}
                            data-reveal
                        >
                            <h3 className="fx-skill-card__title">{group.title}</h3>
                            <ul className="fx-chips">
                                {group.items.map((item) => (
                                    <li key={item} className="fx-chip">{item}</li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            </div>
            <div className="fx-marquee" aria-hidden="true">
                <div className="fx-marquee__track">
                    {marqueeItems.map((item) => (
                        <span key={item.id} className="fx-marquee__item">
                            {item.skill}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}
